/**
 * Feature Gates initialization for the TWG Microsite.
 *
 * The microsite renders for both anonymous visitors (the marketing /
 * logged-out home view) and authenticated Atlassian users. Both
 * audiences need `fg(...)` to resolve against real Statsig values so
 * that gated UI evaluates correctly and Statsig records exposure
 * events for non-logged-in users.
 *
 * Strategy:
 *   1. Bootstrap the gate client with anonymous identifiers at app
 *      start (called from `index.tsx` before React mounts).
 *   2. Re-identify it via `FeatureGates.updateUser` once the user
 *      logs in (called from `App.tsx`).
 *
 * The microsite never swaps identities in-session — logout triggers a
 * full page navigation — so we don't try to handle account-switch
 * flows here. If that ever changes, this file is the place to add it.
 */
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	type ClientOptions,
	type Identifiers,
	FeatureGateEnvironment,
	PerimeterType,
} from '@atlaskit/feature-gate-js-client/types';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags/setBooleanFeatureFlagResolver';
import { initAnalyticsClient } from '@atlassian/twg-microsite-experience/analytics-client';
import { getConfig } from '@atlassian/twg-microsite-experience/config';

// https://hello.help.atlassian.cloud/servicedesk/customer/portal/3450/MHD-18131?created=true
const TARGET_APP = 'twg-microsite-bifrost_web';
// Give the SDK's five-second request timeout time to settle before releasing the UI.
const INITIALIZATION_TIMEOUT_MS = 6000;

/**
 * Gates the resolver always reports as `true`, regardless of Statsig
 * state. Previously force-enabled by an inline resolver in
 * `index.tsx`; centralising them here means the Statsig-backed
 * resolver below honours them too, even before the SDK has loaded.
 */
const ALWAYS_ON_GATES = new Set<string>();
const DEBUG_PANEL_OVERRIDE_GATES = new Set<string>([
	'twg_microsite_pricing_faqs',
	'twg_microsite_standalone_mcp_page',
]);

const getFeatureGateEnvironment = (): FeatureGateEnvironment =>
	getConfig().env === 'production'
		? FeatureGateEnvironment.Production
		: FeatureGateEnvironment.Staging;

/**
 * `perimeter` is typed as required so the same object can be passed
 * unmodified to `FeatureGates.updateUser`, whose first parameter
 * (`FetcherOptions`) requires a defined perimeter.
 */
const featureGatesOptions: ClientOptions & { perimeter: PerimeterType } = {
	apiKey: getConfig().featureGateApiKey,
	environment: getFeatureGateEnvironment(),
	targetApp: TARGET_APP,
	perimeter: PerimeterType.COMMERCIAL,
};

const getLocalGateOverride = (gateName: string): boolean | undefined => {
	if (!DEBUG_PANEL_OVERRIDE_GATES.has(gateName)) {
		return undefined;
	}
	try {
		const override = FeatureGates.getOverrides().gates[gateName];
		return typeof override === 'boolean' ? override : undefined;
	} catch {
		return undefined;
	}
};

// In-flight promises act as idempotency guards. Both are stored as
// never-rejecting promises (errors are caught in `runAndLog`) so a
// later caller can safely `await` them regardless of SDK failure.
let initPromise: Promise<void> | null = null;
let identifyPromise: Promise<void> | null = null;
let resolverInstalled = false;

/** Install the `fg(...)` resolver exactly once. */
const installResolver = (): void => {
	if (resolverInstalled) {
		return;
	}
	setBooleanFeatureFlagResolver((gateName) => {
		if (ALWAYS_ON_GATES.has(gateName)) {
			return true;
		}
		const localOverride = getLocalGateOverride(gateName);
		if (localOverride !== undefined) {
			return localOverride;
		}
		// Legitimate use of `FeatureGates.checkGate` — this is the
		// implementation `fg(...)` delegates to. Wrapped in try/catch
		// so a failed SDK init can't crash gate evaluation.
		try {
			return FeatureGates.checkGate(gateName);
		} catch {
			return false;
		}
	});
	resolverInstalled = true;
};

/**
 * Bound SDK initialization independently of its request timeout so a stalled
 * SDK cannot block the UI. This releases callers without cancelling the SDK.
 */
const runAndLog = async (label: string, op: () => Promise<unknown>): Promise<void> => {
	let timeout: ReturnType<typeof setTimeout> | undefined;
	try {
		installResolver();
		const deadline = new Promise<never>((_, reject) => {
			timeout = setTimeout(() => {
				reject(new Error(`${label} timed out after ${INITIALIZATION_TIMEOUT_MS}ms`));
			}, INITIALIZATION_TIMEOUT_MS);
		});
		await Promise.race([op(), deadline]);
		// eslint-disable-next-line no-console
		console.debug(`[FeatureGates] ${label} complete`);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(`[FeatureGates] ${label} failed:`, error);
	} finally {
		if (timeout !== undefined) {
			clearTimeout(timeout);
		}
	}
};

/**
 * Bootstrap the gate client with anonymous identifiers. Idempotent.
 * Start from the entry point and await in UserProvider so logged-out views
 * evaluate gates only after initialization settles, with a spinner while waiting.
 *
 * Statsig requires at least one supported identifier (a bare `{}`
 * yields a 400 "Must provide at least one Statsig-supported
 * identifier" from the gateway). We reuse the AnalyticsWebClient's
 * anonymous id so anonymous gate evaluations and analytics exposure
 * events bucket against the same stable per-browser id.
 *
 * Installs the `fg(...)` resolver synchronously before the Statsig request.
 * Initialization errors are caught so the app can still render with
 * fallback gate values if the request fails.
 */
export const initializeFeatureGatesAnonymous = (): Promise<void> => {
	if (initPromise === null) {
		initPromise = runAndLog('Anonymous initialization', () => {
			const identifiers: Identifiers = {
				analyticsAnonymousId: initAnalyticsClient().getAnonymousId(),
			};
			return FeatureGates.initialize(featureGatesOptions, identifiers);
		});
	}
	return initPromise;
};

/**
 * Re-identify the gate client with a logged-in user's identifiers.
 * Idempotent: subsequent calls return the in-flight / resolved
 * promise. The microsite never swaps identities in-session, so we
 * don't try to handle account- or org-switch flows.
 *
 * @param accountId - The user's Atlassian account ID for targeting
 * @param orgId - The user's Atlassian org ID (required for org-targeted gates)
 */
export const initializeFeatureGates = (accountId: string, orgId: string): Promise<void> => {
	if (identifyPromise !== null) {
		return identifyPromise;
	}

	identifyPromise = initializeFeatureGatesAnonymous().then(() =>
		runAndLog('Identified initialization', () => {
			const identifiers: Identifiers = {
				analyticsAnonymousId: initAnalyticsClient().getAnonymousId(),
				atlassianAccountId: accountId,
				atlassianOrgId: orgId,
			};
			return FeatureGates.updateUser(featureGatesOptions, identifiers);
		}),
	);

	return identifyPromise;
};
